import { useState, useEffect, useCallback, useMemo, memo } from "react"
import { DayCell } from "./DayCell"
import { Button } from "../ui/Button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { format } from "date-fns"
import { useCalendar } from "../../hooks/useCalendar"
import { logger } from "../../utils/logger"
import { cn } from "../../utils/cn"
import { useEventsStore } from "../../stores/eventsStore"

interface CalendarProps {
  view: "care" // tylko siatka miesiąca (Opieka)
  selectedChild: string
  selectedDate?: Date | null
  onDayClick: (date: Date) => void
  onDayDoubleClick?: (date: Date) => void
}

export const Calendar = memo(function Calendar({ selectedChild, selectedDate, onDayClick, onDayDoubleClick }: CalendarProps) {
  const {
    currentDate,
    monthName,
    nextMonth,
    prevMonth
  } = useCalendar()
  const { fetchEvents } = useEventsStore()
  
  const [dragStart, setDragStart] = useState<Date | null>(null)
  const [dragEnd, setDragEnd] = useState<Date | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Używamy useMemo dla today, żeby nie tworzyć nowego obiektu przy każdym renderze
  const today = useMemo(() => new Date(), [])

  const navigateMonth = useCallback((direction: "prev" | "next") => {
    if (direction === "prev") {
      prevMonth()
    } else {
      nextMonth()
    }
  }, [prevMonth, nextMonth])

  const getMonthDays = useCallback(() => {
    // Generujemy dni bezpośrednio z currentDate, bez zależności od entries
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const firstDayWeekday = firstDayOfMonth.getDay()
    const adjustedFirstDayWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1
    const firstDisplayDay = new Date(firstDayOfMonth)
    firstDisplayDay.setDate(firstDayOfMonth.getDate() - adjustedFirstDayWeekday)
    const lastDisplayDay = new Date(lastDayOfMonth)
    const lastDayWeekday = lastDayOfMonth.getDay()
    const adjustedLastDayWeekday = lastDayWeekday === 0 ? 6 : lastDayWeekday - 1
    const remainingDays = 6 - adjustedLastDayWeekday
    lastDisplayDay.setDate(lastDayOfMonth.getDate() + remainingDays)
    
    const days = []
    const currentDay = new Date(firstDisplayDay)
    while (currentDay <= lastDisplayDay) {
      days.push(new Date(currentDay))
      currentDay.setDate(currentDay.getDate() + 1)
    }
    return days
  }, [currentDate])

  const days = useMemo(() => getMonthDays(), [getMonthDays])

  // Doładuj wydarzenia dla zakresu widocznych dni (widok miesiąca lub tygodnia)
  useEffect(() => {
    const childId = selectedChild
    if (!childId) return
    if (days.length === 0) return
    const startISO = new Date(days[0])
    const endISO = new Date(days[days.length - 1])
    // Normalizuj do pełnych dni
    startISO.setHours(0,0,0,0)
    endISO.setHours(23,59,59,999)
    fetchEvents(childId, startISO.toISOString(), endISO.toISOString())
  }, [selectedChild, days])

  const handleDayClick = useCallback((date: Date) => {
    onDayClick(date)
  }, [onDayClick])

  const handleDayDoubleClick = useCallback((date: Date) => {
    if (onDayDoubleClick) onDayDoubleClick(date)
  }, [onDayDoubleClick])

  const handleMouseDown = useCallback((date: Date) => {
    setDragStart(date)
    setIsDragging(true)
  }, [])

  const handleMouseEnter = useCallback((date: Date) => {
    if (isDragging && dragStart) {
      setDragEnd(date)
    }
  }, [isDragging, dragStart])

  const handleMouseUp = useCallback(() => {
    if (dragStart && dragEnd) {
      logger.debug("Zakres wybrany:", dragStart, "do", dragEnd)
      // TODO: Implement range selection for calendar entries
    }
    setDragStart(null)
    setDragEnd(null)
    setIsDragging(false)
  }, [dragStart, dragEnd])

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp()
      }
    }

    document.addEventListener("mouseup", handleGlobalMouseUp)
    return () => document.removeEventListener("mouseup", handleGlobalMouseUp)
  }, [isDragging, handleMouseUp])

  const weekDays = ["Pn", "Wt", "Śr", "Cz", "Pt", "Sb", "Nd"]

  // Zoptymalizowane renderowanie komórek dni - DayCell pobiera dane bezpośrednio ze store
  const dayCells = useMemo(() => {
    const selectedDateString = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null
    return days.map((date, index) => {
      const dateString = format(date, 'yyyy-MM-dd')
      const isCurrentMonth = currentDate.getMonth() === date.getMonth() && currentDate.getFullYear() === date.getFullYear()
      
      return (
        <DayCell
          key={`${dateString}-${index}`}
          date={date}
          isCurrentMonth={isCurrentMonth}
          isToday={date.toDateString() === today.toDateString()}
          selectedChild={selectedChild}
          isSelected={selectedDateString === dateString}
          onClick={handleDayClick}
          onDoubleClick={handleDayDoubleClick}
          onMouseDown={handleMouseDown}
          onMouseEnter={handleMouseEnter}
          isDragSelected={
            !!(dragStart &&
            dragEnd &&
            date >= new Date(Math.min(dragStart.getTime(), dragEnd.getTime())) &&
            date <= new Date(Math.max(dragStart.getTime(), dragEnd.getTime())))}
        />
      )
    })
  }, [days, currentDate, today, selectedChild, selectedDate, handleDayClick, handleMouseDown, handleMouseEnter, dragStart, dragEnd])
  // DayCell pobiera dane o location/comment bezpośrednio ze store - brak re-renderu przy zmianie entries

  return (
    <div className="card-glass overflow-hidden">
      {/* Calendar Header */}
      <div className="flex items-center justify-between px-3 py-1.5 sm:p-6 border-b border-white/20">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth("prev")}
          className="border-0 bg-transparent hover:bg-slate-100 active:bg-slate-200 transition-colors duration-150 rounded-full p-2"
        >
          <ChevronLeft className="h-5 w-5 text-slate-600" />
        </Button>

        <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
          {monthName}
        </h2>

        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth("next")}
          className="border-0 bg-transparent hover:bg-slate-100 active:bg-slate-200 transition-colors duration-150 rounded-full p-2"
        >
          <ChevronRight className="h-5 w-5 text-slate-600" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="p-1 sm:p-3 md:p-6">
        {/* Week day headers */}
        <div className="calendar-header grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-4">
          {weekDays.map((day) => {
            const isWeekend = day === "Sb" || day === "Nd"
            return (
              <div
                key={day}
                className={cn(
                  "text-center text-xs sm:text-sm font-semibold py-1 sm:py-3 rounded-lg",
                  isWeekend 
                    ? "text-slate-700 bg-gradient-to-b from-slate-200 to-slate-300" 
                    : "text-slate-600 bg-gradient-to-b from-slate-50 to-slate-100"
                )}
              >
                {day}
              </div>
            )
          })}
        </div>

        {/* Calendar days with CSS Subgrid */}
        <div className="calendar-grid grid grid-cols-7 gap-1 sm:gap-3">
          {dayCells}
        </div>
      </div>
    </div>
  )
})

export default Calendar 